import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { AzureGroupScopeMappingDto } from 'src/api/FiretrolApiClient';
import {
  useCreateAzureGroupSyncMapping,
  useUpdateAzureGroupSyncMapping,
} from 'src/api/queries/admin/useAzureGroupSyncMutations';
import { getErrorMessage } from 'src/utils';
import { ModalButton } from 'src/design/molecules/ModalButton';

const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const validationSchema = Yup.object().shape({
  scope: Yup.string().required('Scope is required').max(255),
  azureGroupId: Yup.string()
    .required('Azure Group ID is required')
    .matches(
      GUID_REGEX,
      'Must be a valid GUID (e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)',
    ),
});

interface AzureGroupSyncMappingDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  mapping: AzureGroupScopeMappingDto | null;
}

export const AzureGroupSyncMappingDialog: React.FC<
  AzureGroupSyncMappingDialogProps
> = ({ open, onClose, onSave, mapping }) => {
  const [error, setError] = React.useState<string | null>(null);
  const createMapping = useCreateAzureGroupSyncMapping();
  const updateMapping = useUpdateAzureGroupSyncMapping();

  const formik = useFormik({
    initialValues: {
      scope: mapping?.scope ?? '',
      azureGroupId: mapping?.azureGroupId ?? '',
      isEnabled: mapping?.isEnabled ?? true,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setError(null);
      try {
        if (mapping) {
          await updateMapping.mutateAsync({
            id: mapping.id,
            request: {
              scope: values.scope,
              azureGroupId: values.azureGroupId,
              isEnabled: values.isEnabled,
            },
          });
        } else {
          await createMapping.mutateAsync({
            scope: values.scope,
            azureGroupId: values.azureGroupId,
          });
        }
        onSave();
      } catch (err: unknown) {
        setError(getErrorMessage(err));
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  return (
    <BaseModal
      opened={open}
      onClose={handleClose}
      title={mapping ? 'Edit Scope Mapping' : 'Add Scope Mapping'}
      size="lg"
      centered
    >
      <Box>
        <Stack>
          <TextInput
            label="Scope"
            placeholder="e.g. jobs, settings_all, sys_admin"
            required
            value={formik.values.scope}
            onChange={(e) => formik.setFieldValue('scope', e.target.value)}
            onBlur={() => formik.setFieldTouched('scope')}
            error={formik.touched.scope && formik.errors.scope}
            onKeyDown={handleKeyDown}
          />

          <TextInput
            label="Azure Group ID"
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            required
            value={formik.values.azureGroupId}
            onChange={(e) =>
              formik.setFieldValue('azureGroupId', e.target.value)
            }
            onBlur={() => formik.setFieldTouched('azureGroupId')}
            error={formik.touched.azureGroupId && formik.errors.azureGroupId}
            onKeyDown={handleKeyDown}
            ff="monospace"
          />

          {mapping && (
            <Switch
              label="Enabled"
              checked={formik.values.isEnabled}
              onChange={(e) =>
                formik.setFieldValue('isEnabled', e.currentTarget.checked)
              }
            />
          )}

          {error && (
            <Alert color="red" title="Error">
              {error}
            </Alert>
          )}
        </Stack>
      </Box>

      <Group justify="flex-end" mt="md">
        <ModalButton onClick={handleClose} color="gray" variant="light">
          Cancel
        </ModalButton>
        <ModalButton
          onClick={() => formik.handleSubmit()}
          loading={formik.isSubmitting}
          disabled={!formik.isValid || (!!mapping && !formik.dirty)}
        >
          {mapping ? 'Save Changes' : 'Add Mapping'}
        </ModalButton>
      </Group>
    </BaseModal>
  );
};
